/**
 * Intelligence Engine — Computes signals across shows, music, and finance.
 *
 * Produces typed IntelligenceSignal objects that the Intelligence dashboard
 * renders in panels. Each signal is factual and comparative, never prescriptive.
 */

import { parseLocalDate, shortDateDisplay } from './dateUtils'

// ── Types ──────────────────────────────────────────────────────────

export type SignalType =
  | 'at-risk'
  | 'pacing'
  | 'bonus'
  | 'cannibalization'
  | 'streaming'
  | 'financial'
  | 'needs-attention'

export type SignalSeverity = 'critical' | 'warning' | 'info'
export type SignalSource = 'shows' | 'music' | 'finance' | 'external'

export interface IntelligenceSignal {
  id: string
  type: SignalType
  severity: SignalSeverity
  source: SignalSource
  title: string
  detail: string
  comparison?: string
  delta?: number
  timestamp: string
  relatedEntityId?: string
  actionType?: string
  link?: string
  meta?: Record<string, any>
}

export interface ShowData {
  id: string
  date?: string
  perf_date?: string
  title?: string
  venue?: string
  city?: string
  state?: string
  ticket_count?: number
  tix_sold?: number
  tix_sold_percent?: number
  sellable_cap?: string
  capacity?: number
  guarantee?: string
  bonus?: string
  status?: string
  show_time?: string
  event_title?: string
  ticket_tracking?: boolean
  raw?: any
}

export interface SongData {
  id: string
  song_title?: string
  title?: string
  streams_total?: number
  streams_manual_override?: number
  streams_today?: number
  release_date?: string
  artist_name?: string
  status?: string
  raw?: any
}

export interface FinanceData {
  id: string
  date: string
  due_date?: string
  type: 'revenue' | 'spend'
  amount: number
  description?: string
  source: string
  income_type?: string
  /** Set when merging multi-artist digest data for per-team filtering. */
  digestContextArtistId?: string
}

export interface TaskData {
  id: string
  title: string
  due_date?: string
  status: string
  priority?: string
  category?: string
}

// ── Helpers ─────────────────────────────────────────────────────────

function getDaysUntil(dateStr: string): number {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const date = parseLocalDate(dateStr)
  if (!date) return Number.MAX_SAFE_INTEGER
  date.setHours(0, 0, 0, 0)
  return Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
}

function parseGuarantee(g?: string): number {
  if (!g) return 0
  return Number(g.replace(/[^0-9.]/g, '')) || 0
}

export function parseBonusThreshold(bonus?: string): { amount: number; threshold: number } | null {
  if (!bonus) return null
  const match = bonus.match(/\$?([\d,]+)\s*(?:after|at|@)\s*([\d,]+)/i)
  if (!match) return null
  return {
    amount: Number(match[1].replace(/,/g, '')) || 0,
    threshold: Number(match[2].replace(/,/g, '')) || 0,
  }
}

function haversineDistance(
  lat1: number, lon1: number,
  lat2: number, lon2: number,
): number {
  const R = 3959 // miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

// Rough city→lat/lng lookup for cannibalization (US majors)
const CITY_COORDS: Record<string, [number, number]> = {
  'los angeles': [34.05, -118.24],
  'la': [34.05, -118.24],
  'new york': [40.71, -74.01],
  'nyc': [40.71, -74.01],
  'brooklyn': [40.65, -73.95],
  'chicago': [41.88, -87.63],
  'houston': [29.76, -95.37],
  'phoenix': [33.45, -112.07],
  'san francisco': [37.77, -122.42],
  'sf': [37.77, -122.42],
  'san diego': [32.72, -117.16],
  'dallas': [32.78, -96.8],
  'austin': [30.27, -97.74],
  'denver': [39.74, -104.99],
  'morrison': [39.65, -105.19],
  'seattle': [47.61, -122.33],
  'portland': [45.52, -122.68],
  'miami': [25.76, -80.19],
  'atlanta': [33.75, -84.39],
  'nashville': [36.16, -86.78],
  'boston': [42.36, -71.06],
  'philadelphia': [39.95, -75.17],
  'detroit': [42.33, -83.05],
  'minneapolis': [44.98, -93.27],
  'oakland': [37.8, -122.27],
  'orange county': [33.72, -117.83],
  'anaheim': [33.84, -117.91],
  'long beach': [33.77, -118.19],
  'santa ana': [33.75, -117.87],
  'inglewood': [33.96, -118.35],
  'pasadena': [34.15, -118.14],
}

function cityCoords(city?: string): [number, number] | null {
  if (!city) return null
  const key = city.toLowerCase().trim()
  return CITY_COORDS[key] || null
}

// ── Signal Generators ───────────────────────────────────────────────

function generateShowRiskSignals(shows: ShowData[]): IntelligenceSignal[] {
  const signals: IntelligenceSignal[] = []
  const now = new Date().toISOString()

  for (const show of shows) {
    const showDate = show.date || show.perf_date
    if (!showDate) continue
    const daysUntil = getDaysUntil(showDate)
    if (daysUntil < 0) continue
    const raw = show.raw || {}
    const label = show.title || show.venue || 'Show'

    // Critical: show tomorrow
    if (daysUntil <= 1) {
      signals.push({
        id: `risk-tomorrow-${show.id}`,
        type: 'at-risk',
        severity: 'critical',
        source: 'shows',
        title: label,
        detail: `Show ${daysUntil === 0 ? 'today' : 'tomorrow'} — ${shortDateDisplay(showDate)}`,
        timestamp: now,
        relatedEntityId: show.id,
        actionType: 'show-imminent',
        link: '/shows',
        meta: { daysUntil, city: show.city },
      })
    }

    // Critical: low ticket sales close to show
    if (daysUntil <= 7 && show.tix_sold_percent != null && show.tix_sold_percent < 40) {
      signals.push({
        id: `risk-tickets-${show.id}`,
        type: 'at-risk',
        severity: 'critical',
        source: 'shows',
        title: label,
        detail: `${show.tix_sold_percent}% sold with ${daysUntil} day${daysUntil === 1 ? '' : 's'} to go`,
        timestamp: now,
        relatedEntityId: show.id,
        actionType: 'low-ticket-sales',
        link: '/shows',
        meta: { daysUntil, percent: show.tix_sold_percent, city: show.city },
      })
    }

    // Red flag
    if (raw.red_flag) {
      signals.push({
        id: `risk-flag-${show.id}`,
        type: 'at-risk',
        severity: 'critical',
        source: 'shows',
        title: label,
        detail: raw.red_flag_notes || 'Show flagged',
        timestamp: now,
        relatedEntityId: show.id,
        actionType: 'red-flag',
        link: '/shows',
        meta: { city: show.city },
      })
    }

    // Warning: below 50% with < 30 days
    if (
      daysUntil <= 30 &&
      daysUntil > 7 &&
      show.tix_sold_percent != null &&
      show.tix_sold_percent < 50
    ) {
      signals.push({
        id: `risk-pacing-${show.id}`,
        type: 'at-risk',
        severity: 'warning',
        source: 'shows',
        title: label,
        detail: `${show.tix_sold_percent}% sold — ${daysUntil} days until show`,
        timestamp: now,
        relatedEntityId: show.id,
        actionType: 'below-target',
        link: '/shows',
        meta: { daysUntil, percent: show.tix_sold_percent, city: show.city },
      })
    }

    // Overdue deposit
    if (raw.deposit_due && !raw.deposit_received) {
      const dueDate = parseLocalDate(raw.deposit_due)
      if (dueDate && dueDate < new Date()) {
        signals.push({
          id: `risk-deposit-${show.id}`,
          type: 'at-risk',
          severity: 'critical',
          source: 'shows',
          title: label,
          detail: `Deposit overdue since ${shortDateDisplay(raw.deposit_due)}`,
          timestamp: now,
          relatedEntityId: show.id,
          actionType: 'overdue-deposit',
          link: '/shows',
          meta: { city: show.city },
        })
      }
    }
  }

  return signals
}

function generateBonusSignals(shows: ShowData[]): IntelligenceSignal[] {
  const signals: IntelligenceSignal[] = []
  const now = new Date().toISOString()

  for (const show of shows) {
    const parsed = parseBonusThreshold(show.bonus)
    if (!parsed) continue
    const sold = show.tix_sold ?? show.ticket_count ?? 0
    const remaining = parsed.threshold - sold
    const label = show.title || show.venue || 'Show'

    if (remaining > 0 && remaining <= Math.ceil(parsed.threshold * 0.1)) {
      signals.push({
        id: `bonus-near-${show.id}`,
        type: 'bonus',
        severity: 'warning',
        source: 'shows',
        title: label,
        detail: `${sold.toLocaleString()} sold — ${remaining.toLocaleString()} more for $${parsed.amount.toLocaleString()} bonus`,
        comparison: `Threshold: ${parsed.threshold.toLocaleString()} tickets`,
        timestamp: now,
        relatedEntityId: show.id,
        link: '/shows',
        meta: { sold, threshold: parsed.threshold, bonusAmount: parsed.amount, city: show.city },
      })
    }

    if (remaining <= 0) {
      signals.push({
        id: `bonus-hit-${show.id}`,
        type: 'bonus',
        severity: 'info',
        source: 'shows',
        title: label,
        detail: `Bonus unlocked — $${parsed.amount.toLocaleString()} earned at ${parsed.threshold.toLocaleString()} tickets`,
        timestamp: now,
        relatedEntityId: show.id,
        link: '/shows',
        meta: { sold, threshold: parsed.threshold, bonusAmount: parsed.amount, city: show.city },
      })
    }
  }

  return signals
}

function generateCannibalizationSignals(shows: ShowData[]): IntelligenceSignal[] {
  const signals: IntelligenceSignal[] = []
  const now = new Date().toISOString()
  const checked = new Set<string>()

  const upcoming = shows.filter((s) => {
    const d = getDaysUntil(s.date || s.perf_date || '')
    return d >= 0 && d <= 120
  })

  for (let i = 0; i < upcoming.length; i++) {
    for (let j = i + 1; j < upcoming.length; j++) {
      const a = upcoming[i]
      const b = upcoming[j]
      const key = [a.id, b.id].sort().join('-')
      if (checked.has(key)) continue
      checked.add(key)

      const coordsA = cityCoords(a.city)
      const coordsB = cityCoords(b.city)
      if (!coordsA || !coordsB) continue

      const dist = haversineDistance(coordsA[0], coordsA[1], coordsB[0], coordsB[1])
      if (dist > 75) continue // > 75 miles apart

      const dateA = parseLocalDate(a.date || a.perf_date)
      const dateB = parseLocalDate(b.date || b.perf_date)
      if (!dateA || !dateB) continue

      const daysBetween = Math.abs(
        Math.ceil((dateA.getTime() - dateB.getTime()) / (1000 * 60 * 60 * 24)),
      )
      if (daysBetween > 21) continue // > 3 weeks apart

      const labelA = a.title || a.venue || a.city || 'Show'
      const labelB = b.title || b.venue || b.city || 'Show'

      signals.push({
        id: `cannibal-${key}`,
        type: 'cannibalization',
        severity: 'warning',
        source: 'shows',
        title: `${labelA} / ${labelB}`,
        detail: `${Math.round(dist)} mi apart, ${daysBetween} days between shows`,
        comparison: `${a.city || '?'} and ${b.city || '?'} may split the same audience`,
        timestamp: now,
        meta: { showA: a.id, showB: b.id, distance: dist, daysBetween },
      })
    }
  }

  return signals
}

function generateNeedsAttentionSignals(shows: ShowData[]): IntelligenceSignal[] {
  const signals: IntelligenceSignal[] = []
  const now = new Date().toISOString()

  for (const show of shows) {
    const showDate = show.date || show.perf_date
    if (!showDate) continue
    const daysUntil = getDaysUntil(showDate)
    if (daysUntil < 0) continue
    const raw = show.raw || {}
    const label = show.title || show.venue || 'Show'

    if ((show.status === 'confirmed' || show.status === 'announced') && !raw.guarantee) {
      signals.push({
        id: `attn-guarantee-${show.id}`,
        type: 'needs-attention',
        severity: 'warning',
        source: 'shows',
        title: label,
        detail: `Missing guarantee — ${shortDateDisplay(showDate)}`,
        timestamp: now,
        relatedEntityId: show.id,
        actionType: 'missing-guarantee',
        link: '/shows',
        meta: { city: show.city },
      })
    }

    if (show.status === 'confirmed' && !raw.promoter_contact_emails && !raw.promoter_contact_names) {
      signals.push({
        id: `attn-contact-${show.id}`,
        type: 'needs-attention',
        severity: 'warning',
        source: 'shows',
        title: label,
        detail: `Missing promoter contact — ${shortDateDisplay(showDate)}`,
        timestamp: now,
        relatedEntityId: show.id,
        actionType: 'missing-promoter-contact',
        link: '/shows',
        meta: { city: show.city },
      })
    }

    if (show.status === 'announced' && !show.sellable_cap) {
      signals.push({
        id: `attn-cap-${show.id}`,
        type: 'needs-attention',
        severity: 'warning',
        source: 'shows',
        title: label,
        detail: `Missing sellable capacity — ${shortDateDisplay(showDate)}`,
        timestamp: now,
        relatedEntityId: show.id,
        actionType: 'missing-capacity',
        link: '/shows',
        meta: { city: show.city },
      })
    }

    if (daysUntil <= 14 && !raw.show_time) {
      signals.push({
        id: `attn-time-${show.id}`,
        type: 'needs-attention',
        severity: 'info',
        source: 'shows',
        title: label,
        detail: `Missing show time — ${daysUntil} days out`,
        timestamp: now,
        relatedEntityId: show.id,
        actionType: 'missing-show-time',
        link: '/shows',
        meta: { city: show.city },
      })
    }
  }

  return signals
}

function generateMusicSignals(songs: SongData[]): IntelligenceSignal[] {
  const signals: IntelligenceSignal[] = []
  const now = new Date().toISOString()

  for (const song of songs) {
    const name = song.song_title || song.title || 'Track'
    const raw = song.raw || {}
    const total = song.streams_manual_override ?? song.streams_total ?? 0

    // Streaming: show songs that have daily streams OR meaningful total streams
    if (song.streams_today && song.streams_today > 0) {
      signals.push({
        id: `stream-${song.id}`,
        type: 'streaming',
        severity: 'info',
        source: 'music',
        title: name,
        detail: `${song.streams_today.toLocaleString()} streams today`,
        comparison: total ? `${total.toLocaleString()} total streams` : undefined,
        timestamp: now,
        relatedEntityId: song.id,
        link: '/music',
        meta: { streamsToday: song.streams_today, streamsTotal: total },
      })
    } else if (total > 0) {
      signals.push({
        id: `stream-${song.id}`,
        type: 'streaming',
        severity: 'info',
        source: 'music',
        title: name,
        detail: `${total.toLocaleString()} total streams`,
        timestamp: now,
        relatedEntityId: song.id,
        link: '/music',
        meta: { streamsToday: 0, streamsTotal: total },
      })
    }

    // PRO registration risk for upcoming releases (demos are not scheduled releases)
    if (song.status === 'unreleased' && song.release_date) {
      const daysUntil = getDaysUntil(song.release_date)
      if (daysUntil >= 0 && daysUntil <= 30 && !raw.registered_to_pro) {
        signals.push({
          id: `music-pro-${song.id}`,
          type: 'at-risk',
          severity: 'warning',
          source: 'music',
          title: name,
          detail: `No PRO registration — releasing ${shortDateDisplay(song.release_date)} (${daysUntil}d)`,
          timestamp: now,
          relatedEntityId: song.id,
          actionType: 'no-pro-registration',
          link: '/music',
        })
      }

      const hasMaster = raw.master_contributors?.length > 0
      const hasPub = raw.publishing_contributors?.length > 0
      if (!hasMaster || !hasPub) {
        signals.push({
          id: `music-splits-${song.id}`,
          type: 'needs-attention',
          severity: 'warning',
          source: 'music',
          title: name,
          detail: `Missing ${!hasMaster && !hasPub ? 'master & publishing' : !hasMaster ? 'master' : 'publishing'} splits — releasing ${shortDateDisplay(song.release_date)}`,
          timestamp: now,
          relatedEntityId: song.id,
          actionType: 'missing-splits',
          link: '/music',
        })
      }
    }
  }

  return signals
}

function generatePacingSignals(shows: ShowData[]): IntelligenceSignal[] {
  const signals: IntelligenceSignal[] = []
  const now = new Date().toISOString()

  const upcoming = shows.filter((s) => {
    const st = (s.status || '').toLowerCase().trim()
    if (st === 'canceled' || st === 'cancelled') return false
    const d = getDaysUntil(s.date || s.perf_date || '')
    return d >= 0 && d <= 90 && (s.status === 'announced' || s.status === 'confirmed' || s.status === 'on-sale')
  })

  for (const show of upcoming) {
    const showDate = show.date || show.perf_date
    if (!showDate) continue
    const daysUntil = getDaysUntil(showDate)
    const sold = show.tix_sold ?? show.ticket_count ?? 0
    const cap = Number(String(show.sellable_cap || show.capacity || 0).replace(/[^0-9]/g, '')) || 0
    if (cap <= 0) continue
    if (show.ticket_tracking === false) continue

    const percent = Math.round((sold / cap) * 100)
    const label = show.title || show.venue || 'Show'
    const city = show.city ? ` — ${show.city}` : ''

    let comparison: string | undefined
    let severity: SignalSeverity = 'info'

    // Project whether they'll sell out based on linear pace
    const totalDays = 90 // assume ~90 day on-sale window
    const elapsed = Math.max(1, totalDays - daysUntil)
    const dailyRate = sold / elapsed
    const projected = Math.round(dailyRate * totalDays)

    if (projected >= cap) {
      comparison = `On pace to sell out at current velocity (${Math.round(dailyRate)}/day)`
    } else if (percent < 30 && daysUntil < 30) {
      comparison = `At current pace, projected ${projected.toLocaleString()} of ${cap.toLocaleString()} (${Math.round((projected / cap) * 100)}%)`
      severity = 'warning'
    } else {
      comparison = `Projected ${projected.toLocaleString()} of ${cap.toLocaleString()} at current pace`
    }

    signals.push({
      id: `pace-${show.id}`,
      type: 'pacing',
      severity,
      source: 'shows',
      title: `${label}${city}`,
      detail: `${sold.toLocaleString()} sold of ${cap.toLocaleString()} (${percent}%)`,
      comparison,
      delta: percent,
      timestamp: now,
      relatedEntityId: show.id,
      link: '/shows',
      meta: { city: show.city, daysUntil, percent, sold, cap },
    })
  }

  return signals
}

function generateFinancialSignals(
  shows: ShowData[],
  finances: FinanceData[],
): IntelligenceSignal[] {
  const signals: IntelligenceSignal[] = []
  const now = new Date().toISOString()

  // Revenue from upcoming shows (guarantees)
  let totalGuarantees = 0
  let totalBonusPotential = 0
  const upcoming = shows.filter((s) => {
    const d = getDaysUntil(s.date || s.perf_date || '')
    return d >= 0 && d <= 90
  })

  for (const show of upcoming) {
    totalGuarantees += parseGuarantee(show.guarantee)
    const bp = parseBonusThreshold(show.bonus)
    if (bp) totalBonusPotential += bp.amount
  }

  if (totalGuarantees > 0) {
    signals.push({
      id: 'fin-guarantees-90d',
      type: 'financial',
      severity: 'info',
      source: 'finance',
      title: 'Upcoming Guarantees',
      detail: `$${totalGuarantees.toLocaleString()} in guaranteed revenue across ${upcoming.length} shows (next 90 days)`,
      timestamp: now,
      link: '/money',
      meta: { total: totalGuarantees, showCount: upcoming.length },
    })
  }

  if (totalBonusPotential > 0) {
    signals.push({
      id: 'fin-bonus-potential',
      type: 'financial',
      severity: 'info',
      source: 'finance',
      title: 'Bonus Potential',
      detail: `$${totalBonusPotential.toLocaleString()} in bonus potential across upcoming shows`,
      timestamp: now,
      link: '/money',
      meta: { total: totalBonusPotential },
    })
  }

  // Recent spend summary
  const now30 = new Date()
  now30.setDate(now30.getDate() - 30)
  const recentSpend = finances
    .filter((f) => f.type === 'spend' && parseLocalDate(f.date)! >= now30)
    .reduce((s, f) => s + f.amount, 0)
  const recentRevenue = finances
    .filter((f) => f.type === 'revenue' && parseLocalDate(f.date)! >= now30)
    .reduce((s, f) => s + f.amount, 0)

  if (recentSpend > 0 || recentRevenue > 0) {
    signals.push({
      id: 'fin-30d-summary',
      type: 'financial',
      severity: recentSpend > recentRevenue ? 'warning' : 'info',
      source: 'finance',
      title: '30-Day Cash Flow',
      detail: `$${recentRevenue.toLocaleString()} in — $${recentSpend.toLocaleString()} out`,
      comparison: `Net: ${recentRevenue >= recentSpend ? '+' : '-'}$${Math.abs(recentRevenue - recentSpend).toLocaleString()}`,
      delta: recentRevenue - recentSpend,
      timestamp: now,
      link: '/money',
      meta: { revenue: recentRevenue, spend: recentSpend },
    })
  }

  return signals
}

// ── Main Entry Point ────────────────────────────────────────────────

export interface IntelligenceInput {
  shows: ShowData[]
  songs: SongData[]
  finances: FinanceData[]
  tasks: TaskData[]
}

export function computeIntelligence(input: IntelligenceInput): IntelligenceSignal[] {
  const { shows, songs, finances } = input

  const signals: IntelligenceSignal[] = [
    ...generateShowRiskSignals(shows),
    ...generatePacingSignals(shows),
    ...generateBonusSignals(shows),
    ...generateCannibalizationSignals(shows),
    ...generateNeedsAttentionSignals(shows),
    ...generateMusicSignals(songs),
    ...generateFinancialSignals(shows, finances),
  ]

  // Sort: critical first, then warning, then info
  const severityOrder: Record<string, number> = { critical: 0, warning: 1, info: 2 }
  signals.sort((a, b) => (severityOrder[a.severity] ?? 9) - (severityOrder[b.severity] ?? 9))

  return signals
}

// Re-export for convenience
export type { IntelligenceSignal as Signal }
